import { upsertWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import {
	ErrorModal,
	getMomentTz,
	AssetTreePicker,
	useToken,
	HelmetDexteel,
	MESApiService,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	IconButton,
	TextField,
	Tooltip,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/lab/Alert";

import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import { INITIAL_VALUES_MODAL } from "../models/ModalInitialValue";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import { CraftPicker } from "./picker/craftPicker";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { mesfColors } from "@setup/ag-grid/mesf-theme";
import { TableWorkOrdesResoures } from "./work-order-resources/table-work-ordes-resoures";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: ModalWorkOrder | null;
	initialNewMode?: boolean;
	suffixTitle?: string;
	// Hide the "go to source logbook entry" bookmark. Used when the modal is
	// opened from within the logbook, where linking back to it is redundant.
	hideSourceEntryLink?: boolean;
};

export function EditWorkOrderModal({
	show,
	onHide,
	data,
	initialNewMode = false,
	suffixTitle,
	hideSourceEntryLink = false,
}: Props) {
	const { getUserName } = useToken();

	const {
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderOpenedBy,
				WorkOrderName,
				AssetName,
				AssetId,
				WorkOrderId,
			},
		},
		state,
		actions: {
			setComments,
			setWorkOrderNumberOfWorkers,
			setTemplateTitle,
			setWorkOrderPlannedHours,
			setWorkOrderOpenedBy,
			setOpenTimestamp,
			setWorkOrderName,
			setTitle,
			setAssetName,
			setWorkOrderId,
			setDownHours,
			setAttachmentsLinked,
			setCraftId,
			setAssetId,
			setWorkOrderStatusCode,
			setSourceEntryId,
			setTypeCode,
			setWorkOrderAttachmentsList,
			setWorkOrderDrawingsList,
			setWorkOrderLOTOList,
			setWorkOrderPartsList,
		},
	} = useWorkOrderModalsContext();

	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ModalWorkOrder>({ defaultValues: INITIAL_VALUES_MODAL });

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [userInput, setUserInput] = useState<string>("");
	const [openWorkOrdersResources, setOpenWorkOrdersResources] = useState(false);
	const [newMode, setNewMode] = useState(initialNewMode);
	const [successMessage, setSuccessMessage] = useState("");

	const navigate = useNavigate();
	// Resolve the source logbook entry by work order id so the bookmark link
	// works from any modal, independent of which list SP fed the grid row.
	const { data: sourceEntry } = useQuery({
		queryKey: ["woSourceEntry", WorkOrderId],
		queryFn: async () => {
			const resp = await new MESApiService().callV2(
				"[MMS].[GetWorkOrderSourceEntry]",
				[{ name: "WorkOrderId", value: WorkOrderId as number }],
			);
			return get(resp, "data.tables[0].rows[0]", null) as {
				EntryId: number;
				ShiftId: number;
				Title: string;
			} | null;
		},
		enabled: !!WorkOrderId && show && !hideSourceEntryLink,
	});
	const [assetChanged, setAssetChanged] = useState(false);

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setAssetId(value);
			GetAssetFullPath(value.toString());
			setAssetChanged(true);
			setWorkOrderAttachmentsList([]);
			setWorkOrderDrawingsList([]);
			setWorkOrderLOTOList([]);
			setWorkOrderPartsList([]);
		}
	};

	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (
			Comments === "" ||
			Comments === null ||
			Comments === "null" ||
			Comments === "undefined" ||
			Comments === undefined
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				Comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}

		if (newComment != "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSaveSuccess(false);
	};

	const handleCancel = async () => {
		setUserInput("");
		reset();
		onHide(false);
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const title = watch("Title");
	const changeTitle = () => {
		setTitle(title);
	};

	const downHours = watch("DownHours");
	const changeDownHours = () => {
		setDownHours(downHours);
	};

	const workOrderNumberOfWorkers = watch("WorkOrderNumberOfWorkers");
	const changeWorkOrderNumberOfWorkers = () => {
		setWorkOrderNumberOfWorkers(workOrderNumberOfWorkers);
	};

	const workOrderPlannedHours = watch("WorkOrderPlannedHours");
	const changeWorkOrderPlannedHours = () => {
		setWorkOrderPlannedHours(workOrderPlannedHours);
	};

	const craftId = watch("CraftId");
	const changeCraftId = () => {
		setCraftId(craftId);
	};
	const assetId = watch("AssetId");
	const changeAssetId = () => {
		setAssetId(assetId);
	};

	const handleWorkOrderResourcesDialog = () => {
		setOpenWorkOrdersResources(true);
	};

	const onSubmit = async () => {
		setIsSubmitLoading(true);
		const res = await upsertWorkOrder(
			state.modalWorkOrderSelected as WorkOrder,
		);
		if (res.ok) {
			setAssetChanged(false);
			setSaveSuccess(true);
			setIsSubmitLoading(false);
			setSuccessMessage(
				newMode
					? "Work order created successfully"
					: "Work order edited successfully",
			);
			if (newMode) {
				setNewMode(false);
			}
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
			onHide(false);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			setAssetId(data?.AssetId);
			setWorkOrderStatusCode(data?.WorkOrderStatusCode);
			setComments(data?.Comments);
			setTemplateTitle(data?.TemplateTitle);
			setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			setOpenTimestamp(data?.OpenTimestamp);
			setWorkOrderName(data?.WorkOrderName);
			setWorkOrderId(data?.WorkOrderId);
			setTitle(data?.Title);
			setDownHours(data?.DownHours);
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			setSourceEntryId(data?.SourceEntryId ?? null);
			setAttachmentsLinked(data?.AttachmentsLinked);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			reset(data);
			setWorkOrderAttachmentsList([]);
			setWorkOrderDrawingsList([]);
			setWorkOrderLOTOList([]);
			setWorkOrderPartsList([]);
			setUserInput("");
			setIsSubmitLoading(false);
			setNewMode(initialNewMode);
		}
	}, [show, initialNewMode]);

	useEffect(() => {
		changeTitle();
	}, [title]);
	useEffect(() => {
		changeDownHours();
	}, [downHours]);
	useEffect(() => {
		changeCraftId();
	}, [craftId]);
	useEffect(() => {
		changeWorkOrderNumberOfWorkers();
	}, [workOrderNumberOfWorkers]);
	useEffect(() => {
		changeWorkOrderPlannedHours();
	}, [workOrderPlannedHours]);
	useEffect(() => {
		changeAssetId();
	}, [assetId]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${newMode ? "New Work Order" : "Edit Work Order"}${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle
						sx={{
							padding: "15px 24px 10px 24px",
							width: "100%",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<div style={{ flex: 1, minWidth: 0 }}>
							<Typography sx={{ fontSize: "1.7em", fontWeight: "bold" }}>
								{WorkOrderName ? `WORK ORDER: ${WorkOrderName}` : "WORK ORDER"}
							</Typography>
							{TemplateTitle && <Typography>{data?.TemplateTitle}</Typography>}
						</div>
						{sourceEntry && !hideSourceEntryLink && (
							<Tooltip title={`Go to logbook entry: "${sourceEntry.Title}"`}>
								<IconButton
									type="button"
									size="small"
									onClick={() => {
										navigate(
											`/logbook?mode=shift&shiftId=${sourceEntry.ShiftId}&entryId=${sourceEntry.EntryId}`,
										);
										onHide(false);
									}}
									sx={{
										color: mesfColors.success.pastel,
										flexShrink: 0,
										mr: "-5px",
									}}
								>
									<BookmarkIcon />
								</IconButton>
							</Tooltip>
						)}
					</DialogTitle>
					<DialogContent dividers>
						<Grid2 container spacing={2}>
							<Grid2 sx={{ padding: 0, marginTop: "-5px" }} size={{ xs: 12 }}>
								<Controller
									name="Title"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Title"
											variant="outlined"
											fullWidth
											multiline
											maxRows={1}
											error={!!errors.Title}
											margin="dense"
											onKeyDown={handleTitleKeyDown}
											autoComplete="off"
											value={value}
											onChange={(e) => onChange(e)}
											sx={{
												"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
													{
														border: "2px solid #3F51B5",
													},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2
								container
								spacing={2}
								size={{ xs: 12 }}
								sx={{ paddingBottom: 0.5 }}
							>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Controller
										name="AssetId"
										control={control}
										render={({ field: { value } }) => {
											return (
												<AssetTreePicker
													label="Asset"
													value={value || 0}
													onChange={changeSearchAsset}
												/>
											);
										}}
									/>
								</Grid2>

								<Grid2 size={{ md: 6, xs: 12 }}>
									<TextField
										label="Opened by"
										variant="outlined"
										disabled
										value={WorkOrderOpenedBy}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid2>
							</Grid2>
							<Grid2
								container
								spacing={2}
								size={{ xs: 12 }}
								sx={{ marginTop: "-20px" }}
							>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 2, xs: 6 }}>
									<Controller
										name="WorkOrderPlannedHours"
										control={control}
										rules={{
											pattern: {
												value: /^-?\d*(.\d+)?$/,
												message: "Enter a valid number",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<TextField
												label="Planned Hours"
												variant="outlined"
												error={!!errors.WorkOrderPlannedHours}
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															border: "2px solid #3F51B5",
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ md: 2, xs: 6 }}>
									<Controller
										name="WorkOrderNumberOfWorkers"
										control={control}
										render={({ field: { onChange, value } }) => (
											<TextField
												type="number"
												label="Workers"
												variant="outlined"
												error={!!errors.WorkOrderNumberOfWorkers}
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															border: "2px solid #3F51B5",
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 2, xs: 6 }}>
									<Controller
										name="DownHours"
										control={control}
										render={({ field: { onChange, value } }) => (
											<TextField
												label="Down Hours"
												variant="outlined"
												error={!!errors.DownHours}
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															border: "2px solid #3F51B5",
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 3, xs: 6 }}>
									<Controller
										name="CraftId"
										control={control}
										render={({ field: { onChange, value } }) => (
											<CraftPicker
												value={value as number}
												onChange={onChange}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 3, xs: 6 }}>
									<TextField
										label="Opened"
										variant="outlined"
										disabled
										value={moment(data?.OpenTimestamp).format(
											"MM/DD/YYYY HH:mm A",
										)}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid2>
							</Grid2>
						</Grid2>
						<Grid2 container justifyContent="flex-end" size={{ xs: 12 }}>
							<Grid2
								sx={{
									marginBottom: 1.25,
									marginTop: 2.75,
									padding: 0,
								}}
								size={{ xs: 12 }}
							>
								<Card
									variant="outlined"
									sx={{
										backgroundColor: newMode ? "#fff" : "#E9F2F8",
										border: newMode ? "2px solid #3F51B5" : undefined,
										"& .input-group-text": {
											display: "none",
										},
										"& .MuiDialogContent-dividers": {
											padding: 0,
										},
									}}
								>
									<CardContent>
										<Typography
											sx={{
												fontSize: 15,
												fontWeight: "bold",
												color: "#0000008A",
											}}
										>
											Comments:
										</Typography>
										{/* editable only on a new WO; read-only history otherwise */}
										<SimpleTextAreaControl
											value={Comments || ""}
											onChange={changeComments}
											disabled={!newMode}
											showColor={false}
											minRows={3}
											maxRows={12}
											styleText={{
												border: "none",
												backgroundColor: newMode ? "#fff" : "#E9F2F8",
												fontSize: 16,
											}}
										/>
									</CardContent>
								</Card>
							</Grid2>
							{/* append box only for an existing WO */}
							{!newMode && (
								<Grid2
									container
									spacing={2}
									alignItems="center"
									size={{ xs: 12 }}
								>
									<Grid2 size={{ md: 9, xs: 12 }}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={userInput}
											onChange={(e) => setUserInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
											sx={{
												"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
													{
														border: "1px solid #3F51B5",
													},
											}}
										/>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											sx={{ height: "40px", marginTop: 0.25 }}
										>
											Add comment
										</Button>
									</Grid2>
								</Grid2>
							)}
						</Grid2>
					</DialogContent>
					<DialogActions
						sx={{
							padding: "0px 24px",
							marginTop: "24px",
							paddingBottom: "16px",
						}}
					>
						<Grid2 container spacing={2}>
							<Grid2
								sx={{ display: "flex", alignItems: "center" }}
								title={
									assetChanged
										? "You need to save to be able to manage resources"
										: ""
								}
								size={{ md: 4, xs: 4 }}
							>
								{newMode == false && (
									<Button
										fullWidth
										variant="contained"
										color="primary"
										disabled={assetChanged}
										onClick={handleWorkOrderResourcesDialog}
										title={
											assetChanged
												? "You need to save to be able to manage resources"
												: ""
										}
									>
										MANAGE RESOURCES
									</Button>
								)}
							</Grid2>
							<Grid2
								container
								justifyContent="flex-end"
								spacing={2}
								sx={{ margin: 0 }}
								size={{ md: 8, xs: 8 }}
							>
								<Grid2 size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										CANCEL
									</Button>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										SAVE
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</DialogActions>
				</form>
			</Dialog>
			<TableWorkOrdesResoures
				show={openWorkOrdersResources}
				onHide={(shouldUpdate: boolean) => {
					setOpenWorkOrdersResources(false);
				}}
				AssetId={AssetId}
				WorkOrderId={WorkOrderId}
				WorkOrderName={data?.WorkOrderName as string}
				Title={data?.Title as string}
				AssetName={(data?.AssetName as string)?.replace(/.*\/(.+)$/, "$1")}
				TypeCode={data?.TypeCode as string}
				CraftId={data?.CraftId as number}
				OpenTimestamp={data?.OpenTimestamp as Date}
				CloseTimestamp={data?.CloseTimestamp as Date}
			/>
			<Snackbar
				open={saveSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					{successMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
